import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  Building2,
  ChevronDown,
  Check,
  FilePlus2,
  HelpCircle,
  LogOut,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
  User,
  X,
} from "lucide-react";
import { AES, enc } from "crypto-js";
import { BASE_URL } from "../../screens/Dashboard/CommonApi";
import "./ModernDashboard.css";
import {
  clearOrganizationSession,
  getActiveOrganization,
  getOrganizationIdentity,
  getStoredOrganizations,
  setActiveOrganization,
  setOrganizations,
} from "../../utils/organizationContext";
import { hardRefreshPage } from "../../utils/hardRefresh";
import { getTimeGreeting } from "../../utils/timeGreeting";

const quickActions = [
  { label: "New Invoice", route: "InvoicesNew" },
  { label: "New Purchase", route: "PurchaseOrderNew" },
  { label: "New Customer", route: "CustomerNew" },
];

const searchableModules = [
  {
    module: "Customers",
    route: "Customers",
    endpoint: "/getAllCustomerName",
    title: (row) => row.customerName || row.companyName,
    meta: (row) => [row.email, row.mobile || row.workphone].filter(Boolean).join(" • "),
  },
  {
    module: "Vendors",
    route: "vendors",
    endpoint: "/fetchVendors",
    title: (row) => row.vendorDisplayName || row.vendorName || row.companyName,
    meta: (row) => [row.vendorEmail, row.vendorPhone1].filter(Boolean).join(" • "),
  },
  {
    module: "Invoices",
    route: "Invoices",
    endpoint: "/fetchInvoice",
    title: (row) => row.invoiceNumber,
    meta: (row) => [row.customerName, row.total ? `₹ ${row.total}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Quotations",
    route: "quotes",
    endpoint: "/fetchQuote",
    title: (row) => row.quoteNumber,
    meta: (row) => [row.customerName, row.total ? `₹ ${row.total}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Estimates",
    route: "Estimates",
    endpoint: "/fetchEstimate",
    title: (row) => row.estimateNumber,
    meta: (row) => [row.customerName, row.total ? `₹ ${row.total}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Sales Orders",
    route: "Order",
    endpoint: "/fetchSalesOrder",
    title: (row) => row.salesOrder || row.salesOrderNumber,
    meta: (row) => [row.customerName, row.total ? `₹ ${row.total}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Products",
    route: "items",
    endpoint: "/getAllItemData",
    extraBody: { vendorId: "" },
    title: (row) => row.name || row.itemDetails,
    meta: (row) => [row.type, row.salesPrice ? `₹ ${row.salesPrice}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Expenses",
    route: "expenses",
    endpoint: "/fetchExpenses",
    title: (row) => row.invoice || row.expenseAccount,
    meta: (row) => [row.expenseAccount, row.amount ? `₹ ${row.amount}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Banking",
    route: "bankacc",
    endpoint: "/fetchBanks",
    title: (row) => row.bankName || row.name,
    meta: (row) => [row.accountType, row.ifsc].filter(Boolean).join(" • "),
  },
  {
    module: "Payments",
    route: "Payment",
    endpoint: "/fetchPaymentReceived2",
    title: (row) => row.paymentNumber || row.invoiceNumber || row.customerName,
    meta: (row) => [row.customerName, row.amountReceived ? `₹ ${row.amountReceived}` : ""].filter(Boolean).join(" • "),
  },
  {
    module: "Reports",
    route: "reports",
    local: true,
    records: [
      { title: "Profit and Loss", meta: "Financial report", route: "ProfitAndLoss" },
      { title: "Balance Sheet", meta: "Financial report", route: "BalanceSheetSchedule" },
      { title: "Sales by Customer", meta: "Sales report", route: "SalesCustomer" },
      { title: "Item Stocks", meta: "Inventory report", route: "ItemStocks" },
    ],
  },
];

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const getUniqueId = () => {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  if (!encryptedUniqueId) return "";
  return AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8);
};

const ModernHeader = ({ userName = "Admin", navigation }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("zarpTheme");
    if (savedTheme === "dark" || savedTheme === "light") return savedTheme;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeResult, setActiveResult] = useState(0);
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const [addOrganizationOpen, setAddOrganizationOpen] = useState(false);
  const [addOrganizationForm, setAddOrganizationForm] = useState({ organizationName: "", companyName: "" });
  const [addOrganizationError, setAddOrganizationError] = useState("");
  const [addOrganizationLoading, setAddOrganizationLoading] = useState(false);
  const [organizations, setOrganizationsState] = useState(() => getStoredOrganizations());
  const [activeOrganizationState, setActiveOrganizationState] = useState(() => getActiveOrganization());
  const profileRef = useRef(null);
  const actionsRef = useRef(null);
  const notificationsRef = useRef(null);
  const helpRef = useRef(null);
  const searchRef = useRef(null);
  const organizationRef = useRef(null);
  const searchInputRef = useRef(null);
  const activeOrganizationName =
    activeOrganizationState?.organizationName ||
    activeOrganizationState?.companyName ||
    "Organization";
  const canRenderPortal = typeof document !== "undefined";
  const greeting = getTimeGreeting();

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("zarpTheme", theme);
  }, [theme]);

  const recentResults = useMemo(
    () => [
      { id: "dashboard", title: "Dashboard", module: "Navigation", route: "dashboard", meta: "Business overview" },
      { id: "customers", title: "Customers", module: "Navigation", route: "Customers", meta: "CRM and receivables" },
      { id: "invoices", title: "Invoices", module: "Navigation", route: "Invoices", meta: "Sales billing" },
      { id: "items", title: "Items", module: "Navigation", route: "items", meta: "Products and services" },
      { id: "banking", title: "Banking", module: "Navigation", route: "bankacc", meta: "Bank accounts" },
      { id: "reports", title: "Reports", module: "Navigation", route: "reports", meta: "Financial reports" },
    ],
    []
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }

      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setActionsOpen(false);
      }

      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }

      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setHelpOpen(false);
      }

      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }

      if (organizationRef.current && !organizationRef.current.contains(event.target)) {
        setOrganizationOpen(false);
      }
    };

    const handleEscape = (event) => {
      const isSearchShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (isSearchShortcut) {
        event.preventDefault();
        setSearchOpen(true);
        setProfileOpen(false);
        setActionsOpen(false);
        setTimeout(() => searchInputRef.current?.focus(), 0);
        return;
      }

      if (event.key === "Escape") {
        setProfileOpen(false);
        setActionsOpen(false);
        setNotificationsOpen(false);
        setHelpOpen(false);
        setSearchOpen(false);
        setOrganizationOpen(false);
        setAddOrganizationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [searchOpen]);

  useEffect(() => {
    const syncOrganizations = () => {
      setOrganizationsState(getStoredOrganizations());
      setActiveOrganizationState(getActiveOrganization());
    };

    window.addEventListener("storage", syncOrganizations);
    window.addEventListener("zarp:organization-change", syncOrganizations);
    return () => {
      window.removeEventListener("storage", syncOrganizations);
      window.removeEventListener("zarp:organization-change", syncOrganizations);
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) return;

    const query = searchQuery.trim().toLowerCase();
    setActiveResult(0);

    if (!query) {
      setSearchResults(recentResults);
      setSearchLoading(false);
      return;
    }

    const timeout = setTimeout(async () => {
      const jwtToken = localStorage.getItem("jwtToken");
      const uniqueId = getUniqueId();
      setSearchLoading(true);

      try {
        const localMatches = searchableModules
          .filter((moduleConfig) => moduleConfig.local)
          .flatMap((moduleConfig) =>
            moduleConfig.records
              .filter((record) => `${record.title} ${record.meta}`.toLowerCase().includes(query))
              .map((record, index) => ({
                id: `${moduleConfig.module}-${index}`,
                module: moduleConfig.module,
                title: record.title,
                meta: record.meta,
                route: record.route || moduleConfig.route,
              }))
          );

        const remoteResults = await Promise.all(
          searchableModules
            .filter((moduleConfig) => !moduleConfig.local)
            .map(async (moduleConfig) => {
              try {
                const response = await fetch(BASE_URL + moduleConfig.endpoint, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                  },
                  body: JSON.stringify({ uniqueId, ...(moduleConfig.extraBody || {}) }),
                });
                const data = await response.json();
                return normalizeRows(data.responseMessage || data.data || data)
                  .map((row, index) => ({
                    id: `${moduleConfig.module}-${row.id || index}`,
                    module: moduleConfig.module,
                    title: moduleConfig.title(row) || moduleConfig.module,
                    meta: moduleConfig.meta(row),
                    route: moduleConfig.route,
                  }))
                  .filter((result) => `${result.title} ${result.meta} ${result.module}`.toLowerCase().includes(query))
                  .slice(0, 4);
              } catch (error) {
                return [];
              }
            })
        );

        setSearchResults([...localMatches, ...remoteResults.flat()].slice(0, 18));
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [recentResults, searchOpen, searchQuery]);

  const handleLogout = () => {
    ["jwtToken", "userId", "uniqueId1", "refreshToken", "loginDate"].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    clearOrganizationSession();
    navigation.navigate("login");
  };

  const handleOrganizationChange = (organization) => {
    if (!organization) return;
    if (getOrganizationIdentity(organization) === getOrganizationIdentity(activeOrganizationState)) {
      setOrganizationOpen(false);
      return;
    }

    setActiveOrganization(organization, { silent: true });
    hardRefreshPage();
  };

  const openAddOrganization = () => {
    setAddOrganizationForm({ organizationName: "", companyName: "" });
    setAddOrganizationError("");
    setAddOrganizationOpen(true);
    setOrganizationOpen(false);
    setProfileOpen(false);
    setActionsOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setHelpOpen(false);
  };

  const closeAddOrganization = () => {
    if (addOrganizationLoading) return;
    setAddOrganizationOpen(false);
    setAddOrganizationError("");
  };

  const handleAddOrganizationSubmit = async (event) => {
    event.preventDefault();
    const organizationName = addOrganizationForm.organizationName.trim();
    const companyName = addOrganizationForm.companyName.trim() || organizationName;
    if (!organizationName) {
      setAddOrganizationError("Organization name is required.");
      return;
    }
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      setAddOrganizationError("Please login again before creating an organization.");
      return;
    }

    setAddOrganizationLoading(true);
    setAddOrganizationError("");
    try {
      const response = await fetch(`${BASE_URL}/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          organizationName,
          companyName,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status !== "success" || !data.organization) {
        throw new Error(data.responseMessage || "Unable to create organization");
      }

      const nextOrganizations = [
        ...organizations.filter(
          (organization) => getOrganizationIdentity(organization) !== getOrganizationIdentity(data.organization)
        ),
        data.organization,
      ];
      setOrganizations(nextOrganizations);
      setOrganizationsState(nextOrganizations);
      setActiveOrganization(data.organization, { silent: true });
      setAddOrganizationOpen(false);
      hardRefreshPage();
    } catch (error) {
      const message =
        error instanceof TypeError
          ? `Unable to reach backend at ${BASE_URL}. Please start the backend server and check database connection.`
          : error.message || "Unable to create organization. Please try again.";
      setAddOrganizationError(message);
    } finally {
      setAddOrganizationLoading(false);
    }
  };

  const openSearch = () => {
    setSearchOpen(true);
    setProfileOpen(false);
    setActionsOpen(false);
    setNotificationsOpen(false);
    setHelpOpen(false);
  };

  const selectSearchResult = (result) => {
    if (!result?.route) return;
    navigation.navigate(result.route);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveResult((current) => Math.min(current + 1, Math.max(searchResults.length - 1, 0)));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveResult((current) => Math.max(current - 1, 0));
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectSearchResult(searchResults[activeResult]);
    }
  };

  return (
    <header className="zarp-header">
      <div className="zarp-header-copy">
        <h1>{greeting}, {userName}</h1>
        <p>Here's what's happening with your business today.</p>
      </div>

      <div className="zarp-header-actions">
        <div className="zarp-new-wrapper" ref={actionsRef}>
          <button
            className="zarp-new-button"
            aria-expanded={actionsOpen}
            onClick={() => {
              setActionsOpen((open) => !open);
              setProfileOpen(false);
            }}
          >
            <Plus size={16} />
            New
            <ChevronDown size={15} />
          </button>
          {actionsOpen && (
            <div className="zarp-action-menu">
              {quickActions.map((action) => (
                <button
                  key={action.route}
                  onClick={() => {
                    navigation.navigate(action.route);
                    setActionsOpen(false);
                  }}
                >
                  <FilePlus2 size={16} />
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="zarp-search-wrap" ref={searchRef}>
        <label className="zarp-search" onClick={openSearch}>
          <Search size={18} />
          <input
            ref={searchInputRef}
            placeholder="Search anything..."
            value={searchQuery}
            onFocus={openSearch}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <kbd>Ctrl + K</kbd>
        </label>
          {searchOpen && (
            <div className="zarp-search-menu">
              <div className="zarp-search-menu-head">
                <Search size={16} />
                <span>{searchQuery ? "Search results" : "Quick navigation"}</span>
              </div>
              {searchLoading && <div className="zarp-search-empty">Searching...</div>}
              {!searchLoading && searchResults.length === 0 && (
                <div className="zarp-search-empty">No matching records found</div>
              )}
              {!searchLoading &&
                searchResults.map((result, index) => (
                  <button
                    key={result.id}
                    className={index === activeResult ? "is-active" : ""}
                    onMouseEnter={() => setActiveResult(index)}
                    onClick={() => selectSearchResult(result)}
                  >
                    <span>{String(result.module || "?").slice(0, 2).toUpperCase()}</span>
                    <strong>{result.title}</strong>
                    <small>{result.module}{result.meta ? ` • ${result.meta}` : ""}</small>
                  </button>
                ))}
            </div>
          )}
        </div>

        <button
          className="zarp-icon-button zarp-theme-toggle"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="zarp-header-popover" ref={notificationsRef}>
          <button
            className="zarp-icon-button"
            aria-label="Notifications"
            onClick={() => {
              setNotificationsOpen((open) => !open);
              setHelpOpen(false);
              setProfileOpen(false);
            }}
          >
            <Bell size={19} />
          </button>
          {notificationsOpen && (
            <div className="zarp-mini-menu">
              <strong>No notifications</strong>
              <p>You are all caught up right now.</p>
            </div>
          )}
        </div>

        <div className="zarp-header-popover" ref={helpRef}>
          <button
            className="zarp-icon-button"
            aria-label="Help"
            onClick={() => {
              setHelpOpen((open) => !open);
              setNotificationsOpen(false);
              setProfileOpen(false);
            }}
          >
            <HelpCircle size={19} />
          </button>
          {helpOpen && (
            <div className="zarp-mini-menu">
              <strong>Need help?</strong>
              <p>For any help kindly contact work@makeemyapp.in</p>
            </div>
          )}
        </div>

        <div className="zarp-org-tools" ref={organizationRef}>
          {organizations.length > 0 && (
            <div className="zarp-org-switcher">
              <button
                type="button"
                className="zarp-org-trigger"
                aria-haspopup="listbox"
                aria-expanded={organizationOpen}
                onClick={() => {
                  setOrganizationOpen((open) => !open);
                  setProfileOpen(false);
                  setActionsOpen(false);
                  setSearchOpen(false);
                  setNotificationsOpen(false);
                  setHelpOpen(false);
                }}
              >
                <span className="zarp-org-mark">
                  <Building2 size={17} />
                </span>
                <span className="zarp-org-copy">
                  <small>Organization</small>
                  <strong>{activeOrganizationName}</strong>
                </span>
                <ChevronDown size={16} className="zarp-org-chevron" />
              </button>

              {organizationOpen && (
                <div className="zarp-org-menu" role="listbox" aria-label="Switch organization">
                  {organizations.map((organization) => {
                    const organizationIdentity = getOrganizationIdentity(organization);
                    const isActive = organizationIdentity === getOrganizationIdentity(activeOrganizationState);
                    const organizationName =
                      organization.organizationName || organization.companyName || organization.organizationKey;

                    return (
                      <button
                        type="button"
                        key={organizationIdentity}
                        role="option"
                        aria-selected={isActive}
                        className={isActive ? "is-active" : ""}
                        onClick={() => handleOrganizationChange(organization)}
                      >
                        <span className="zarp-org-option-mark">
                          {String(organizationName || "O").slice(0, 1).toUpperCase()}
                        </span>
                        <span>
                          <strong>{organizationName}</strong>
                          <small>{organization.organizationKey || `Organization ${organizationIdentity}`}</small>
                        </span>
                        {isActive && <Check size={17} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <button
            className="zarp-icon-button"
            type="button"
            aria-label="Add organization"
            title="Add organization"
            onClick={openAddOrganization}
          >
            <Plus size={19} />
          </button>
        </div>

        <div className="zarp-profile" ref={profileRef}>
          <button
            className="zarp-profile-trigger"
            aria-expanded={profileOpen}
            onClick={() => {
              setProfileOpen((open) => !open);
              setActionsOpen(false);
            }}
          >
            <span>A</span>
            <ChevronDown size={16} />
          </button>

          {profileOpen && (
            <div className="zarp-profile-menu">
              <div>
                <strong>{userName}</strong>
                <small>admin@zarp.com</small>
              </div>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigation.navigate("profile");
                }}
              >
                <User size={16} />
                Profile
              </button>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigation.navigate("Setting");
                }}
              >
                <Settings size={16} />
                Settings
              </button>
              <button className="is-danger" onClick={handleLogout}>
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {addOrganizationOpen && canRenderPortal && createPortal(
        <div className="zarp-modal-backdrop is-centered" role="presentation">
          <section className="zarp-modal zarp-org-modal" role="dialog" aria-modal="true" aria-labelledby="add-org-title">
            <button
              type="button"
              className="zarp-modal-close"
              aria-label="Close"
              onClick={closeAddOrganization}
              disabled={addOrganizationLoading}
            >
              <X size={18} />
            </button>
            <div className="zarp-modal-head">
              <span className="zarp-modal-icon blue">
                <Building2 size={21} />
              </span>
              <div>
                <h2 id="add-org-title">Create Organization</h2>
                <p>Start a separate workspace with its own profile, records, and settings.</p>
              </div>
            </div>
            <form className="zarp-modal-form" onSubmit={handleAddOrganizationSubmit}>
              <label>
                <span>Organization name</span>
                <input
                  autoFocus
                  value={addOrganizationForm.organizationName}
                  onChange={(event) =>
                    setAddOrganizationForm((current) => ({
                      ...current,
                      organizationName: event.target.value,
                      companyName: current.companyName || event.target.value,
                    }))
                  }
                  placeholder="Example: Makee My App GST"
                />
              </label>
              <label>
                <span>Company name</span>
                <input
                  value={addOrganizationForm.companyName}
                  onChange={(event) =>
                    setAddOrganizationForm((current) => ({ ...current, companyName: event.target.value }))
                  }
                  placeholder="Shown on invoices and reports"
                />
              </label>
              {addOrganizationError && <div className="zarp-modal-error">{addOrganizationError}</div>}
              <div className="zarp-modal-actions">
                <button type="button" className="zarp-modal-secondary" onClick={closeAddOrganization} disabled={addOrganizationLoading}>
                  Cancel
                </button>
                <button type="submit" className="zarp-modal-primary" disabled={addOrganizationLoading}>
                  {addOrganizationLoading ? "Creating..." : "Create Organization"}
                </button>
              </div>
            </form>
          </section>
        </div>,
        document.body
      )}
    </header>
  );
};

export default ModernHeader;
